import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { independentItinerariesService } from '../../services/independent';
import { Plus, Trash2, Download } from 'lucide-react';

// Itineraries are created by converting a sent quote (see IndependentQuotesList),
// matching how agency agents work. This builder is edit-only: it loads an
// existing itinerary by id and lets the agent adjust trip details, components,
// and notes.
const IndependentItineraryBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [itinerary, setItinerary] = useState({
    client_id: '',
    trip_name: '',
    start_date: '',
    end_date: '',
    total_price: 0,
    amount_paid: 0,
    status: 'confirmed',
    notes: '',
    components: []
  });

  const [newComponent, setNewComponent] = useState({
    type: '',
    title: '',
    description: '',
    price: 0,
    booking_reference: '',
    confirmation_number: '',
    status: 'confirmed',
    notes: ''
  });

  useEffect(() => {
    if (id) {
      loadItinerary();
    } else {
      // No standalone creation - send agents to convert a quote instead
      navigate('/independent/itineraries');
    }
  }, [id]);

  const loadItinerary = async () => {
    try {
      setLoading(true);
      const data = await independentItinerariesService.getById(id);

      setItinerary({
        ...data,
        components: typeof data.components === 'string' ? JSON.parse(data.components) : (data.components || [])
      });
    } catch (error) {
      console.error('Error loading itinerary:', error);
      alert('Failed to load itinerary');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItinerary(prev => ({ ...prev, [name]: value }));
  };

  const calculateTotalPrice = (components) => {
    const total = components.reduce((sum, comp) => sum + parseFloat(comp.price || 0), 0);
    setItinerary(prev => ({ ...prev, total_price: total }));
  };

  const addComponent = () => {
    if (!newComponent.title || !newComponent.type) {
      alert('Please fill in component title and type');
      return;
    }

    const updatedComponents = [...itinerary.components, { ...newComponent, id: Date.now().toString() }];
    setItinerary(prev => ({ ...prev, components: updatedComponents }));
    calculateTotalPrice(updatedComponents);

    setNewComponent({
      type: '',
      title: '',
      description: '',
      price: 0,
      booking_reference: '',
      confirmation_number: '',
      status: 'confirmed',
      notes: ''
    });
  };

  const removeComponent = (index) => {
    const updatedComponents = itinerary.components.filter((_, i) => i !== index);
    setItinerary(prev => ({ ...prev, components: updatedComponents }));
    calculateTotalPrice(updatedComponents);
  };

  const handleSave = async () => {
    if (!itinerary.trip_name) {
      alert('Please fill in the trip name');
      return;
    }

    try {
      setLoading(true);
      await independentItinerariesService.update(id, {
        trip_name: itinerary.trip_name,
        start_date: itinerary.start_date,
        end_date: itinerary.end_date,
        total_price: itinerary.total_price,
        status: itinerary.status,
        notes: itinerary.notes,
        components: JSON.stringify(itinerary.components)
      });
      alert('Itinerary updated successfully!');
      navigate('/independent/itineraries');
    } catch (error) {
      console.error('Error saving itinerary:', error);
      alert('Failed to save itinerary');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    window.print();
  };

  if (loading && id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Trip Details' },
    { number: 2, title: 'Components' },
    { number: 3, title: 'Review' }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Edit Itinerary</h1>
          <p className="text-gray-600 mt-1">Manage trip details and travel components</p>
        </div>
        <button
          onClick={() => navigate('/independent/itineraries')}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      {/* Progress Steps */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step.number ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step.number}
                </div>
                <span className="text-sm mt-2">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 ${currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: Trip Details */}
      {currentStep === 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-6">Trip Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip Name *</label>
              <input
                type="text"
                name="trip_name"
                value={itinerary.trip_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={itinerary.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                name="start_date"
                value={itinerary.start_date ? itinerary.start_date.split('T')[0] : ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                name="end_date"
                value={itinerary.end_date ? itinerary.end_date.split('T')[0] : ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={itinerary.notes}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setCurrentStep(2)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Next: Components
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Components */}
      {currentStep === 2 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-6">Travel Components</h2>

          {itinerary.components.length > 0 && (
            <div className="space-y-3 mb-6">
              {itinerary.components.map((component, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{component.type}</span>
                        <span className={`px-2 py-1 text-xs rounded ${
                          component.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {component.status}
                        </span>
                      </div>
                      <h4 className="font-semibold">{component.title}</h4>
                      <p className="text-sm text-gray-600">{component.description}</p>
                      {component.booking_reference && (
                        <p className="text-sm text-gray-600 mt-1">
                          Booking Ref: <span className="font-mono">{component.booking_reference}</span>
                        </p>
                      )}
                      {component.confirmation_number && (
                        <p className="text-sm text-gray-600">
                          Confirmation: <span className="font-mono">{component.confirmation_number}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-lg font-bold text-blue-600">${parseFloat(component.price || 0).toLocaleString()}</p>
                      <button onClick={() => removeComponent(index)} className="mt-2 text-red-600 hover:text-red-800">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-4">Add Component</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={newComponent.type}
                  onChange={(e) => setNewComponent({ ...newComponent, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="hotel">Hotel</option>
                  <option value="flight">Flight</option>
                  <option value="car">Car Rental</option>
                  <option value="cruise">Cruise</option>
                  <option value="activity">Activity/Tour</option>
                  <option value="general">General Item</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newComponent.title}
                  onChange={(e) => setNewComponent({ ...newComponent, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <input
                  type="number"
                  value={newComponent.price}
                  onChange={(e) => setNewComponent({ ...newComponent, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newComponent.status}
                  onChange={(e) => setNewComponent({ ...newComponent, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking Reference</label>
                <input
                  type="text"
                  value={newComponent.booking_reference}
                  onChange={(e) => setNewComponent({ ...newComponent, booking_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Number</label>
                <input
                  type="text"
                  value={newComponent.confirmation_number}
                  onChange={(e) => setNewComponent({ ...newComponent, confirmation_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newComponent.description}
                  onChange={(e) => setNewComponent({ ...newComponent, description: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <button onClick={addComponent} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus size={18} className="inline mr-2" />
              Add Component
            </button>
          </div>

          <div className="flex justify-between mt-6">
            <button onClick={() => setCurrentStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Back
            </button>
            <button onClick={() => setCurrentStep(3)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Next: Review
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {currentStep === 3 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-6">Review & Save</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-3">Trip Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-600">Trip:</span> <span className="font-medium">{itinerary.trip_name}</span></div>
                <div><span className="text-gray-600">Status:</span> <span className="font-medium">{itinerary.status}</span></div>
                <div><span className="text-gray-600">Dates:</span> <span className="font-medium">{itinerary.start_date} to {itinerary.end_date}</span></div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3">Components ({itinerary.components.length})</h3>
              <div className="space-y-2">
                {itinerary.components.map((comp, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{comp.title}</span>
                    <span className="font-medium">${parseFloat(comp.price || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3">Financial Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Price:</span>
                  <span className="text-xl font-bold text-blue-600">
                    ${itinerary.components.reduce((sum, c) => sum + parseFloat(c.price || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Amount Paid:</span>
                  <span className="text-lg font-semibold text-green-600">${parseFloat(itinerary.amount_paid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span>Balance Due:</span>
                  <span className="text-lg font-semibold text-red-600">
                    ${(itinerary.components.reduce((sum, c) => sum + parseFloat(c.price || 0), 0) - parseFloat(itinerary.amount_paid || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button onClick={() => setCurrentStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Back
            </button>
            <div className="flex space-x-3">
              <button onClick={generatePDF} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
                <Download size={18} className="mr-2" />
                Export PDF
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Saving...' : 'Save Itinerary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndependentItineraryBuilder;
